import { supabase } from '@/lib/supabase';
import {
  Product,
  ProductRow,
  ProductsResponse,
  SingleProductResponse,
} from '@/types/product.types';

// Convert database row (snake_case) to app object (camelCase)
function convertRowToProduct(row: ProductRow): Product {
  return {
    id: row.id,
    name: row.name,
    nameEn: row.name_en,
    category: row.category,
    categoryEn: row.category_en,
    image: row.image,
    alt: row.alt,
    description: row.description,
    descriptionEn: row.description_en,
    minOrder: row.min_order,
    minOrderEn: row.min_order_en,
    // Optional fields - only include if they exist
    ...(row.price && { price: row.price }),
    ...(row.price_eur && { priceEur: row.price_eur }),
    ...(row.price_value !== undefined && { priceValue: row.price_value }),
    ...(row.features && { features: row.features }),
    ...(row.features_en && { featuresEn: row.features_en }),
    ...(row.ingredients && { ingredients: row.ingredients }),
    ...(row.ingredients_en && { ingredientsEn: row.ingredients_en }),
    ...(row.calories !== undefined && { calories: row.calories }),
    ...(row.protein !== undefined && { protein: row.protein }),
    ...(row.carbs !== undefined && { carbs: row.carbs }),
    ...(row.fat !== undefined && { fat: row.fat }),
    ...(row.storage && { storage: row.storage }),
    ...(row.storage_en && { storageEn: row.storage_en }),
    ...(row.shelf_life && { shelfLife: row.shelf_life }),
    ...(row.shelf_life_en && { shelfLifeEn: row.shelf_life_en }),
    ...(row.dietary && { dietary: row.dietary }),
    ...(row.created_at && { createdAt: row.created_at }),
    ...(row.updated_at && { updatedAt: row.updated_at }),
  };
}

export const productService = {
  /**
   * Fetch all products with optional filtering
   */
  async getAll(options?: {
    category?: string;
    dietary?: string[];
    priceRange?: 'low' | 'medium' | 'high' | 'all';
    searchQuery?: string;
  }): Promise<ProductsResponse> {
    try {
      let query = supabase
        .from('products')
        .select('*');

      // Apply category filter - CRITICAL: Must match exact database value
      if (options?.category && options.category !== 'Tümü' && options.category !== 'All') {
        query = query.eq('category', options.category);
      }

      // Apply price range filter only if price_value column exists
      if (options?.priceRange && options.priceRange !== 'all') {
        switch (options.priceRange) {
          case 'low':
            query = query.lt('price_value', 50);
            break;
          case 'medium':
            query = query.gte('price_value', 50).lt('price_value', 100);
            break;
          case 'high':
            query = query.gte('price_value', 100);
            break;
        }
      }

      // Apply search query
      if (options?.searchQuery?.trim()) {
        const searchTerm = `%${options.searchQuery.toLowerCase()}%`;
        query = query.or(
          `name.ilike.${searchTerm},name_en.ilike.${searchTerm},description.ilike.${searchTerm},description_en.ilike.${searchTerm}`
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      console.log('Raw Supabase data:', data);
      console.log('Filter options:', options);

      // Convert all rows to Product objects
      const products = data?.map(convertRowToProduct) || [];

      console.log('Converted products:', products);

      // Apply dietary filter in JavaScript (array operations not supported in PostgREST)
      let filteredProducts = products;
      if (options?.dietary && options.dietary.length > 0) {
        filteredProducts = products.filter((product) =>
          product.dietary && options.dietary?.every((diet) => product.dietary.includes(diet))
        );
      }

      return { data: filteredProducts, error: null };
    } catch (error) {
      console.error('Product service error:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch a single product by ID
   */
  async getById(id: string): Promise<SingleProductResponse> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      const product = data ? convertRowToProduct(data) : null;

      return { data: product, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch products by category
   */
  async getByCategory(category: string): Promise<ProductsResponse> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const products = data?.map(convertRowToProduct) || [];

      return { data: products, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Search products by name or description
   */
  async search(searchTerm: string): Promise<ProductsResponse> {
    try {
      const searchPattern = `%${searchTerm.toLowerCase()}%`;
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .or(
          `name.ilike.${searchPattern},name_en.ilike.${searchPattern},description.ilike.${searchPattern},description_en.ilike.${searchPattern}`
        )
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const products = data?.map(convertRowToProduct) || [];

      return { data: products, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};